import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Warning from '@mui/icons-material/Warning';
import Timer from '@mui/icons-material/Timer';

interface SessionTimeoutWarningProps {
  open: boolean;
  remainingSeconds: number;
  onExtend: () => void;
  onLogout: () => void;
}

export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  open,
  remainingSeconds,
  onExtend,
  onLogout,
}) => {
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const progressValue = Math.max(0, (remainingSeconds / 300) * 100); // 5 minutes = 300 seconds

  const formatTime = (mins: number, secs: number) => {
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="warning" />
          <Typography variant="h6" component="span">
            Session Timeout Warning
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your session will expire due to inactivity
        </Alert>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Timer color="action" />
          <Typography variant="body1">
            Time remaining: <strong>{formatTime(minutes, seconds)}</strong>
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progressValue}
          color="warning"
          sx={{
            height: 8,
            borderRadius: 4,
            mb: 2,
          }}
        />

        <Typography variant="body2" color="text.secondary">
          Click "Stay Logged In" to extend your session by 30 minutes, or "Logout" to end your
          session now.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onLogout} variant="outlined" color="inherit">
          Logout Now
        </Button>
        <Button onClick={onExtend} variant="contained" color="primary" autoFocus>
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};
